<template>
  <div class="mod-page">
    <div class="mod-header">
      <div>
        <h1 class="mod-title">Tipos de identificación de empleado</h1>
        <p class="mod-subtitle">Catálogo configurable por empresa para identificaciones oficiales</p>
      </div>
      <div class="mod-header-actions">
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nuevo tipo</span>
        </button>
      </div>
    </div>

    <div class="mod-filters">
      <div class="mod-search">
        <i class="bi bi-search"></i>
        <input v-model="searchText" type="text" placeholder="Buscar por clave, nombre o descripción..." @input="applyFilters">
      </div>
      <div class="mod-filter-right">
        <select v-model="pageSize" class="mod-select" @change="applyFilters">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <div class="mod-table-wrap">
      <table class="mod-table">
        <thead>
          <tr>
            <th class="mod-th-sort" @click="sortBy('Orden')">Orden <i :class="sortIcon('Orden')"></i></th>
            <th class="mod-th-sort" @click="sortBy('Clave')">Clave <i :class="sortIcon('Clave')"></i></th>
            <th class="mod-th-sort" @click="sortBy('Nombre')">Nombre <i :class="sortIcon('Nombre')"></i></th>
            <th>Origen</th>
            <th class="mod-th-sort" @click="sortBy('EsActivo')">Activo <i :class="sortIcon('EsActivo')"></i></th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td colspan="6" class="mod-td-center mod-td-empty">
              <i class="bi bi-inbox"></i>
              <span>{{ searchText ? 'Sin resultados' : 'No hay identificaciones registradas' }}</span>
            </td>
          </tr>
          <tr v-for="item in pagedItems" :key="item.Clave + '-' + item.EmpresaId" class="mod-tr">
            <td>{{ item.Orden }}</td>
            <td><span class="mod-badge">{{ item.Clave }}</span></td>
            <td>
              <strong>{{ item.Nombre }}</strong>
              <div v-if="item.Descripcion" class="mod-td-muted">{{ item.Descripcion }}</div>
            </td>
            <td><span class="tipo-pill">{{ item.EsSistema ? 'Sistema' : 'Empresa' }}</span></td>
            <td>
              <span :class="item.EsActivo ? 'mod-status-active' : 'mod-status-inactive'">
                {{ item.EsActivo ? 'Sí' : 'No' }}
              </span>
            </td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <EmpleadoIdentificacionTipoForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import authService from '@/services/auth';
import empleadoIdentificacionTipoService from './empleadoidentificaciontipo.service';
import EmpleadoIdentificacionTipoForm from './EmpleadoIdentificacionTipoForm.vue';

export default {
  name: 'EmpleadoIdentificacionTipoList',
  components: { EmpleadoIdentificacionTipoForm },
  data() {
    return {
      items: [],
      filteredItems: [],
      searchText: '',
      loading: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      pageSize: 25,
      sortField: 'Orden',
      sortAsc: true
    };
  },
  computed: {
    totalFiltered() { return this.filteredItems.length; },
    pagedItems() { return this.filteredItems.slice(0, this.pageSize); }
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        this.items = await empleadoIdentificacionTipoService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
        this.applyFilters();
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      let data = [...this.items];
      const q = this.searchText.toLowerCase().trim();
      if (q) {
        data = data.filter(i =>
          (i.Clave || '').toLowerCase().includes(q) ||
          (i.Nombre || '').toLowerCase().includes(q) ||
          (i.Descripcion || '').toLowerCase().includes(q)
        );
      }
      data.sort((a, b) => {
        let va = a[this.sortField] ?? '';
        let vb = b[this.sortField] ?? '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });
      this.filteredItems = data;
    },
    sortBy(field) {
      if (this.sortField === field) this.sortAsc = !this.sortAsc;
      else { this.sortField = field; this.sortAsc = true; }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved() {
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`¿Eliminar tipo "${item.Nombre}"?`)) return;
      try {
        const result = await empleadoIdentificacionTipoService.borrar(item);
        if (result.Number >= 0) this.loadItems();
        else alert(result.Message || 'Error al eliminar');
      } catch {
        alert('Error al eliminar');
      }
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

.tipo-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: #edf2f7;
  color: #334155;
  font-size: 0.75rem;
  font-weight: 700;
}
</style>

